import { useCallback, useMemo, useRef } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import { Lead } from "../../../utils/types";
import ChevronRightIcon from "../../../../../assets/svg/chevron-right-icon";
import SortByIcon from "../../../../../assets/svg/sort-by-icon";
import FlagIconCDN from "../../../../../components/FlagIconCDN/FlagIconCDN";
import {
  AgentAssignmentDropdown,
  type AgentUser,
} from "../../AgentAssignmentDropdown";
import { useAssignAgentToLeadMutation } from "../../../../../redux/services/crmApi";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import { ContactOwnerFilter } from "../components/tableFilters/ContactOwnerFilter";
import { NeighbourhoodFilter } from "../components/tableFilters/NeighbourhoodFilter";
import { DatePresetFilter } from "../components/tableFilters/DatePresetFilter";
import LocationIcon from "../../../../../assets/svg/location-icon";
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";
import {
  startOfDay,
  endOfDay,
  subDays,
  subHours,
  subMonths,
  startOfMonth,
  endOfMonth,
} from "date-fns";

const shouldUseNeighbourhoodLabel = () =>
  getClientCRMFlag("useNeighbourhoodLabel");

export const CREATED_DATE_PRESETS = [
  { key: "last-24-hours", label: "Last 24 Hours" },
  { key: "last-7-days", label: "Last 7 Days" },
  { key: "last-30-days", label: "Last 30 Days" },
  { key: "last-month", label: "Last Month" },
  { key: "this-month", label: "This Month" },
];

const DISPLAY_VALUE_MAP: Record<string, string> = {
  "last-24-hours": "Last 24 Hours",
  "last-7-days": "Last 7 Days",
  "last-30-days": "Last 30 Days",
  "last-month": "Last Month",
  "this-month": "This Month",
};

export function getDateRangeFromPreset(
  presetKey: string
): { startDate: string; endDate: string; displayValue: string } | null {
  const now = new Date();

  switch (presetKey) {
    case "last-24-hours":
      return {
        startDate: subHours(now, 24).toISOString(),
        endDate: now.toISOString(),
        displayValue: DISPLAY_VALUE_MAP[presetKey] || presetKey,
      };
    case "last-7-days":
      return {
        startDate: startOfDay(subDays(now, 6)).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: DISPLAY_VALUE_MAP[presetKey] || presetKey,
      };
    case "last-30-days":
      return {
        startDate: startOfDay(subDays(now, 30)).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: DISPLAY_VALUE_MAP[presetKey] || presetKey,
      };
    case "last-month": {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: startOfMonth(lastMonth).toISOString(),
        endDate: endOfMonth(lastMonth).toISOString(),
        displayValue: DISPLAY_VALUE_MAP[presetKey] || presetKey,
      };
    }
    case "this-month":
      return {
        startDate: startOfMonth(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: DISPLAY_VALUE_MAP[presetKey] || presetKey,
      };
    case "today":
      return {
        startDate: startOfDay(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: "Today",
      };
    case "yesterday": {
      const d = subDays(now, 1);
      return {
        startDate: startOfDay(d).toISOString(),
        endDate: endOfDay(d).toISOString(),
        displayValue: "Yesterday",
      };
    }
    case "tomorrow": {
      const d = new Date(now);
      d.setDate(d.getDate() + 1);
      return {
        startDate: startOfDay(d).toISOString(),
        endDate: endOfDay(d).toISOString(),
        displayValue: "Tomorrow",
      };
    }
    case "next_week": {
      const d = new Date(now);
      d.setDate(d.getDate() + 7);
      return {
        startDate: startOfDay(now).toISOString(),
        endDate: endOfDay(d).toISOString(),
        displayValue: "Next Week",
      };
    }
    case "in_two_days": {
      const d = new Date(now);
      d.setDate(d.getDate() + 2);
      return {
        startDate: startOfDay(now).toISOString(),
        endDate: endOfDay(d).toISOString(),
        displayValue: "In Two Days",
      };
    }
    case "last_7_days":
      return {
        startDate: startOfDay(subDays(now, 6)).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: "Last 7 Days",
      };
    case "last_30_days":
      return {
        startDate: startOfDay(subDays(now, 30)).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: "Last 30 Days",
      };
    case "this_month":
      return {
        startDate: startOfMonth(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
        displayValue: "This Month",
      };
    case "last_month": {
      const lm = subMonths(now, 1);
      return {
        startDate: startOfMonth(lm).toISOString(),
        endDate: endOfMonth(lm).toISOString(),
        displayValue: "Last Month",
      };
    }
    default:
      return null;
  }
}

export interface LeadColumnConfig {
  key: string;
  label: string;
  visible: boolean;
}

export const LEAD_COLUMN_REGISTRY: { key: string; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone Number" },
  { key: "agent", label: "Contact Owner" },
  { key: "current_stage", label: "Lead Status" },
  {
    key: "property",
    label: shouldUseNeighbourhoodLabel() ? "Neighbourhood" : "Property",
  },
  { key: "created_at", label: "Created" },
  { key: "updated_at", label: "Last Activity" },
  { key: "destination_country", label: "Destination Country" },
  { key: "destination_city", label: "Destination City" },
];

export const getDefaultColumnConfig = (): LeadColumnConfig[] =>
  LEAD_COLUMN_REGISTRY.map((col) => ({ ...col, visible: true }));

export interface LeadTableSelectionState {
  selectedIds: Set<string>;
  allSelected: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}

export interface LeadTableCallbacks {
  onLeadUpdate?: (lead: Lead, stageId: string) => void;
  stageId?: string;
}

export interface LeadTableFilterState {
  contactOwner: string[];
  neighbourhood: string | null;
  createdDate: string | null;
  lastActivity: string | null;
}

export interface LeadTableFilterCallbacks {
  activeFilter: string | null;
  onToggleFilter: (columnKey: string) => void;
  filterState: LeadTableFilterState;
  onContactOwnerApply: (ids: string[]) => void;
  onContactOwnerReset: () => void;
  onNeighbourhoodSelect: (id: string | null) => void;
  onCreatedDateSelect: (preset: string | null) => void;
  onLastActivitySelect: (preset: string | null) => void;
}

export const INITIAL_FILTER_STATE: LeadTableFilterState = {
  contactOwner: [],
  neighbourhood: null,
  createdDate: null,
  lastActivity: null,
};

const AgentCell = ({
  lead,
  onLeadUpdate,
  stageId,
}: {
  lead: Lead;
  onLeadUpdate?: (lead: Lead, stageId: string) => void;
  stageId?: string;
}) => {
  const [assignAgentToLead, { isLoading: isAssigningAgent }] =
    useAssignAgentToLeadMutation();

  const defaultSelectedAgents = useMemo(() => {
    if (!Array.isArray(lead?.agent) || lead.agent.length === 0) return [];
    return lead.agent.map((a) => ({
      id: a.id,
      name: a.name,
      role: a.role,
      status: a.status,
      profile_picture: a.profile_picture || null,
      email: a.email || "",
    }));
  }, [lead?.agent]);

  const handleAssignAgent = useCallback(
    async (agentIds: string[], agents: AgentUser[]) => {
      try {
        await assignAgentToLead({
          leadId: lead.lead_id,
          agentIds,
        }).unwrap();
        const updatedLead = {
          ...lead,
          agent: agents.map((a) => ({
            id: a.id,
            name: a.name,
            role: a.role,
            status: a.status,
            profile_picture: a.profile_picture,
          })),
        } as Lead;
        onLeadUpdate?.(updatedLead, stageId || "");
        customToast("Agent assigned successfully", "success");
      } catch {
        customToast("Failed to assign agent", "error");
      }
    },
    [assignAgentToLead, lead, onLeadUpdate, stageId]
  );

  return (
    <div className="px-3" onClick={(e) => e.stopPropagation()}>
      <AgentAssignmentDropdown
        selectedAgentIds={
          Array.isArray(lead?.agent) ? lead.agent.map((a) => a.id) : []
        }
        onApply={handleAssignAgent}
        isLoading={isAssigningAgent}
        defaultSelectedAgents={defaultSelectedAgents}
      />
    </div>
  );
};

const FilterableHeader = ({
  label,
  filterKey,
  filters,
  children,
}: {
  label: string;
  filterKey: string;
  filters?: LeadTableFilterCallbacks;
  children: (
    triggerRef: React.RefObject<HTMLDivElement | null>
  ) => React.ReactNode;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className={`text-left px-3 py-2 flex items-center gap-1 ${filters ? "cursor-pointer" : ""}`}
        data-table-filter-trigger
        onClick={(e) => {
          if (!filters) return;
          e.stopPropagation();
          filters.onToggleFilter(filterKey);
        }}
      >
        <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
          {label}
        </span>
        {filters && (
          <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
        )}
      </div>
      {filters?.activeFilter === filterKey && children(triggerRef)}
    </div>
  );
};

export const getFilteredLeadTableColumns = (
  stageColorMap: Record<string, string>,
  stageNameMap: Record<string, string>,
  visibleKeys: string[],
  selection?: LeadTableSelectionState,
  callbacks?: LeadTableCallbacks,
  filters?: LeadTableFilterCallbacks
): ColumnDef<Lead>[] => {
  const allColumns = getLeadTableColumns(
    stageColorMap,
    stageNameMap,
    selection,
    callbacks,
    filters
  );
  const columnMap = new Map(
    allColumns.map((col) => [
      (col as { accessorKey?: string }).accessorKey ?? col.id ?? "",
      col,
    ])
  );
  const filtered = visibleKeys
    .map((key) => columnMap.get(key))
    .filter((col): col is ColumnDef<Lead> => !!col);

  const checkboxCol = columnMap.get("selection");
  const arrowCol = columnMap.get("arrow");
  return [
    ...(checkboxCol ? [checkboxCol] : []),
    ...filtered,
    ...(arrowCol ? [arrowCol] : []),
  ];
};

export const getLeadTableColumns = (
  stageColorMap: Record<string, string>,
  stageNameMap: Record<string, string>,
  selection?: LeadTableSelectionState,
  callbacks?: LeadTableCallbacks,
  filters?: LeadTableFilterCallbacks
): ColumnDef<Lead>[] => {
  const columns: ColumnDef<Lead>[] = [];

  // if (selection) {
  //   columns.push({
  //     id: "selection",
  //     size: 40,
  //     header: () => (
  //       <div className="flex items-center justify-center py-3 px-2">
  //         <input
  //           type="checkbox"
  //           checked={selection.allSelected}
  //           onChange={selection.onToggleAll}
  //           className="w-[10.67px] h-[10.67px] rounded-[3.05px] transition-opacity duration-200 cursor-pointer"
  //         />
  //       </div>
  //     ),
  //     cell: ({ row }) => (
  //       <div
  //         className="flex items-center justify-center h-full"
  //         onClick={(e) => e.stopPropagation()}
  //       >
  //         <input
  //           type="checkbox"
  //           checked={selection.selectedIds.has(row.original?.lead_id)}
  //           onChange={() => selection.onToggle(row.original?.lead_id)}
  //           className="w-[10.67px] h-[10.67px] rounded-[3.05px] transition-opacity duration-200 cursor-pointer"
  //         />
  //       </div>
  //     ),
  //   });
  // }

  columns.push(
    {
      accessorKey: "name",
      minSize: 200,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Name
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const name = row.original?.name || "Anonymous";
        return (
          <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
            <Avatar text={name} size={24} />
            <TruncatedTextTooltip
              text={name}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk font-medium truncate"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      minSize: 220,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Email
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3 max-w-full overflow-hidden">
          <TruncatedTextTooltip
            text={row.original?.email || "-"}
            className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
          />
        </div>
      ),
    },
    {
      accessorKey: "phone",
      minSize: 160,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Phone Number
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const code = row.original?.phone_country_code;
        const phone = row.original?.phone;
        const display = phone ? `${code} ${phone}` : "-";
        return (
          <div className="px-3 max-w-full overflow-hidden">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap truncate block">
              {display}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "agent",
      minSize: 200,
      header: () => (
        // <FilterableHeader label="Contact Owner" filterKey="agent" filters={filters}>
        //   {(triggerRef) => (
        //     <ContactOwnerFilter
        //       triggerRef={triggerRef}
        //       selectedAgentIds={filters!.filterState.contactOwner}
        //       onApply={(ids) => {
        //         filters!.onContactOwnerApply(ids);
        //         filters!.onToggleFilter("agent");
        //       }}
        //       onReset={() => {
        //         filters!.onContactOwnerReset();
        //         filters!.onToggleFilter("agent");
        //       }}
        //       onClose={() => filters!.onToggleFilter("agent")}
        //     />
        //   )}
        // </FilterableHeader>
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Contact Owner
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <AgentCell
          lead={row.original}
          onLeadUpdate={callbacks?.onLeadUpdate}
          stageId={callbacks?.stageId}
        />
      ),
    },
    {
      accessorKey: "current_stage",
      minSize: 150,
      header: () => (
        <div className="text-left py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Lead Status
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const stage = row.original?.current_stage;
        const color = stageColorMap[stage] || "#6b7280";
        const name = stageNameMap[stage] || stage?.toUpperCase();
        return (
          <div className="py-1 px-1.5 w-fit flex items-center gap-2 border border-border-gray dark:border-border-gray-dk rounded-[6px]">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="text-md uppercase font-semibold text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "property",
      minSize: 200,
      header: () => (
        // <FilterableHeader
        //   label={shouldUseNeighbourhoodLabel() ? "Neighbourhood" : "Property"}
        //   filterKey="property"
        //   filters={filters}
        // >
        //   {(triggerRef) => (
        //     <NeighbourhoodFilter
        //       triggerRef={triggerRef}
        //       selectedPropertyId={filters!.filterState.neighbourhood}
        //       onSelect={(id) => {
        //         filters!.onNeighbourhoodSelect(id);
        //         filters!.onToggleFilter("property");
        //       }}
        //     />
        //   )}
        // </FilterableHeader>
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            {shouldUseNeighbourhoodLabel() ? "Neighbourhood" : "Property"}
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const property = row.original?.property;
        const propertyText = Array.isArray(property)
          ? property.join(", ")
          : property;
        return (
          <div className="px-3 max-w-full overflow-hidden">
            <TruncatedTextTooltip
              text={propertyText || "-"}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      minSize: 160,
      header: () => (
        // <FilterableHeader
        //   label="Created Date"
        //   filterKey="created_at"
        //   filters={filters}
        // >
        //   {(triggerRef) => (
        //     <DatePresetFilter
        //       triggerRef={triggerRef}
        //       selectedPreset={filters!.filterState.createdDate}
        //       presets={CREATED_DATE_PRESETS}
        //       onSelect={(preset) => {
        //         filters!.onCreatedDateSelect(preset);
        //         filters!.onToggleFilter("created_at");
        //       }}
        //     />
        //   )}
        // </FilterableHeader>
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Created Date
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const createdAt = row.original?.created_at;
        if (!createdAt) return <div className="px-3">-</div>;
        const { date, time } = getDateTimeByFormat(new Date(createdAt));
        return (
          <div className="px-3">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {date} {time}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      minSize: 160,
      header: () => (
        // <FilterableHeader
        //   label="Last Activity"
        //   filterKey="updated_at"
        //   filters={filters}
        // >
        //   {(triggerRef) => (
        //     <DatePresetFilter
        //       triggerRef={triggerRef}
        //       selectedPreset={filters!.filterState.lastActivity}
        //       onSelect={(preset) => {
        //         filters!.onLastActivitySelect(preset);
        //         filters!.onToggleFilter("updated_at");
        //       }}
        //     />
        //   )}
        // </FilterableHeader>
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Last Activity
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const updatedAt = row.original?.updated_at;
        if (!updatedAt) return <div className="px-3">-</div>;
        const { date, time } = getDateTimeByFormat(new Date(updatedAt));
        return (
          <div className="px-3">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {date} {time}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "destination_country",
      minSize: 180,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Destination Country
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const country = row.original?.destination_country;
        const alpha2 = row.original?.destination_country_alpha2;
        return (
          <div className="px-3 flex items-center gap-2 max-w-full overflow-hidden">
            {alpha2 && (
              <div className="w-[18px] h-[14px] shrink-0">
                <FlagIconCDN
                  code={alpha2}
                  flagIconClassName="w-[18px] h-[14px] shrink-0 border border-gray-75 dark:border-gray-75-dk rounded-[2px]"
                />
              </div>
            )}
            <TruncatedTextTooltip
              text={country || "-"}
              className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "destination_city",
      minSize: 150,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Destination City
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3 max-w-full overflow-hidden flex items-center gap-1">
          {row.original?.destination_city && (
            <LocationIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
          )}
          <TruncatedTextTooltip
            text={row.original?.destination_city || "-"}
            className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate"
          />
        </div>
      ),
    },
    {
      id: "arrow",
      size: 40,
      header: () => <div />,
      cell: () => (
        <div className="flex items-center justify-center px-2">
          <ChevronRightIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
        </div>
      ),
    }
  );

  return columns;
};
